import * as React from "react"
import { useState, useEffect } from "react";
import {hoverButton} from './shared.module.scss'
import { getYardiPropIdFromSlug } from '../utils/propertyMapUtil';
import { getVWOFMEUserId } from '../utils/vwoApplyTest';
import { useVWOApplyTest, useTrackEvent } from '../context/VWOContext';
import { detectButtonPosition, BUTTON_POSITIONS } from '../utils/buttonConfig';
import { buildFindigsUrl } from '../utils/findigsUrlBuilder';
import { getFindigsUrlBySlug } from '../utils/findigsLinks';
import ApplyInterstitialFormRHF from './ApplyInterstitialFormRHF';
import useArrivalInfo from '../utils/useArrivalInfo';
import { hasSubmittedForProperty } from '../utils/interstitialFormTracker';
import { getVWOCampaignGroup, getFlagConfigForGroup } from '../utils/vwoCampaignGroupUtil';


const ApplyButton = ({
    slug, 
    communityName = null, 
    text = "Apply Now",
    marginRight = "1rem",
    isInHeader = "true",
    buttonPosition = null, // Optional override for position
    autoDetect = true, // Enable/disable auto-detection
    itemName = null, // Legacy prop name for floorplan name
    floorplanName = null, // Explicit floorplan name prop
    floorplanBeds = null, // Number of bedrooms from Contentful
    toEmail = null // Property email from Contentful
}) => {
    //console.log("slug from within applyButton ",slug);
    
    const [showInterstitial, setShowInterstitial] = useState(false);
    const [detectedPosition, setDetectedPosition] = useState(BUTTON_POSITIONS.UNKNOWN);
    const [enhancedFindigsUrl, setEnhancedFindigsUrl] = useState('');
    const arrivalInfo = useArrivalInfo();
    
    // Get consistent FME user ID for VWO event tracking (with SSR protection)
    const userId = typeof window !== 'undefined' ? getVWOFMEUserId() : 'ssr-fallback-id';
    
    // Use server-side VWO decision from context
    const { variation: abTestVariation } = useVWOApplyTest();
    
    // Get flag key for current property to pass to Findigs
    const campaignGroup = getVWOCampaignGroup(slug);
    const flagConfig = getFlagConfigForGroup(campaignGroup);
    const flagKey = flagConfig?.flagKey || null;
    
    // Use VWO FME SDK for event tracking (always call the hook, but it returns safe values during SSR)
    const { trackEvent, isReady: trackingReady } = useTrackEvent();
    
    // Helper function to track apply button events using VWO FME SDK
    const trackApplyEvent = (eventName, userIdParam = null, propertySlug = null, buttonPosition = null, additionalData = {}) => {
        if (!trackingReady) {
            console.warn('🎯 VWO FME: trackEvent not ready yet');
            return;
        }
        
        // Use provided parameters or fall back to component state
        const finalUserId = userIdParam || userId;
        const finalPropertySlug = propertySlug || slug;
        const finalButtonPosition = buttonPosition || finalPosition;
        
        // Map old event names to FME-compatible events
        const eventMapping = {
            'button_clicked': 'apply_button_clicked',
            'direct_redirect': 'apply_control_redirect',
            'direct_redirect_repeat': 'apply_repeat_user',
            'form_displayed': 'apply_form_displayed',
            'form_submitted': 'apply_form_completed',
            'form_skipped': 'apply_form_abandoned',
            'form_error': 'apply_form_error', 
            'interstitial_completed': 'apply_treatment_redirect'
        };
        
        const mappedEventName = eventMapping[eventName] || `apply_${eventName}`;
        
        // Get campaign group for this property
        const campaignGroup = getVWOCampaignGroup(finalPropertySlug);
        
        // Only track events for properties in VWO campaigns
        if (!campaignGroup) {
            console.log(`🎯 VWO FME: Property '${finalPropertySlug}' not in any VWO campaign group - skipping event tracking`);
            return;
        }
        
        const eventData = {
            property: finalPropertySlug,
            userId: finalUserId,
            button_position: finalButtonPosition,
            original_event: eventName,
            campaign_type: `fme_apply_interstitial_${campaignGroup}`,
            campaign_group: campaignGroup,
            tracking_source: 'fme_sdk', // Distinguish from SmartCode events
            floorplan_name: floorplanName || itemName,
            floorplan_beds: floorplanBeds,
            community_name: communityName,
            ...additionalData
        };
        
        trackEvent(mappedEventName, eventData).then((result) => {
            console.log(`🎯 VWO FME Event Tracked: ${mappedEventName}`, {
                result,
                eventData,
                trackingReady
            });
        }).catch((error) => {
            console.error('🎯 VWO FME Tracking Error:', error);
        });
        
        // Development logging
        if (process.env.NODE_ENV === 'development') {
            console.log(`📊 FME Apply Button Event: ${mappedEventName}`, eventData);
        }
        
        // Push to GA4 dataLayer for form events
        if (typeof window !== 'undefined' && window.dataLayer) {
            if (eventName === 'form_submitted') {
                window.dataLayer.push({
                    event: 'inter_form_submission',
                    section: 'apply_interstitial',
                    property_slug: finalPropertySlug,
                    button_position: finalButtonPosition,
                    user_id: finalUserId,
                    ...additionalData
                });
                console.log('📊 GA4 Event: inter_form_submission pushed to dataLayer');
            }
            
            if (eventName === 'form_skipped') {
                window.dataLayer.push({
                    event: 'inter_form_abandonment',
                    section: 'apply_interstitial',
                    property_slug: finalPropertySlug,
                    button_position: finalButtonPosition,
                    user_id: finalUserId,
                    ...additionalData
                });
                console.log('📊 GA4 Event: inter_form_abandonment pushed to dataLayer');
            }
        }
    };
    
    // Determine final button position
    const finalPosition = buttonPosition || (autoDetect ? detectedPosition : BUTTON_POSITIONS.UNKNOWN);
    
    useEffect(() => {
        // Auto-detect button position if enabled
        if (autoDetect && !buttonPosition) {
            const detected = detectButtonPosition();
            setDetectedPosition(detected);
            
        }
        
        // Build enhanced Findigs URL with UTM parameters and tracking
        const baseFindigsUrl = getFindigsUrlBySlug(getYardiPropIdFromSlug(slug));
        
        // Use explicit floorplan name (either new prop or legacy itemName)
        const finalFloorplanName = floorplanName || itemName;
        
        const enhancedUrl = buildFindigsUrl(baseFindigsUrl, {
            arrivalInfo,
            buttonPosition: finalPosition,
            floorplanName: finalFloorplanName,
            abTestVariation,
            flagKey
        });
        
        setEnhancedFindigsUrl(enhancedUrl);
        
        // Development logging to verify flag key is being passed
        if (process.env.NODE_ENV === 'development') {
            console.log(`🎯 Findigs URL Enhanced for ${slug}:`, {
                flagKey,
                campaignGroup,
                abTestVariation,
                enhancedUrl
            });
        }
    }, [arrivalInfo, autoDetect, buttonPosition, finalPosition, slug, floorplanName, itemName, abTestVariation, flagKey]);


    
    //console.log('Slug: ' + slug);
    //console.log('Enhanced Findigs URL: ' + enhancedFindigsUrl);

    // Handle button click based on A/B test variation
    const handleButtonClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        
        console.log(`🎯 Apply Button: Using ${abTestVariation} variation`, {
            userId,
            slug,
            position: finalPosition,
            floorplanName: floorplanName || itemName,
            floorplanBeds,
            communityName
        });
        
        if (abTestVariation === 'treatment') {
            // Track button click for A/B test participants
            trackApplyEvent('button_clicked', userId, slug, finalPosition);
            
            // Check if form was already submitted for this property in current session
            const alreadySubmitted = hasSubmittedForProperty(slug);
            
            if (alreadySubmitted) {
                // Skip interstitial form if already submitted for this property
                console.log(`🎯 Apply Button: Skipping interstitial - already submitted for property: ${slug}`);
                trackApplyEvent('direct_redirect_repeat', userId, slug, finalPosition);
                window.location.href = enhancedFindigsUrl;
            } else {
                // Treatment: Show interstitial form (first time for this property)
                trackApplyEvent('form_displayed', userId, slug, finalPosition);
                setShowInterstitial(true);
            }
        } else if (abTestVariation === 'control') {
            // Control: Direct redirect with VWO tracking
            trackApplyEvent('button_clicked', userId, slug, finalPosition);
            trackApplyEvent('direct_redirect', userId, slug, finalPosition);
            window.location.href = enhancedFindigsUrl;
        } else {
            // Not in test: Direct redirect without VWO tracking
            console.log(`🎯 Apply Button: Property not in A/B test - direct redirect without tracking`);
            window.location.href = enhancedFindigsUrl;
        }
    };
    
    const handleInterstitialSubmit = () => {
        trackApplyEvent('interstitial_completed', userId, slug, finalPosition);
        setShowInterstitial(false);
        window.location.href = enhancedFindigsUrl;
    };
    
    const handleInterstitialSkip = () => {
        trackApplyEvent('form_skipped', userId, slug, finalPosition);
        setShowInterstitial(false);
        // Don't redirect - just close the modal
    };
    
    const handleInterstitialClose = () => {
        // Track form abandonment when modal is closed
        trackApplyEvent('form_skipped', userId, slug, finalPosition);
        setShowInterstitial(false);
    };


    const commonProps = {
        className: isInHeader ? "button is-link navbar-item "+hoverButton : "button is-small is-warning",
        id: isInHeader ? "app_now_btn" : "map_app_button",
        style: isInHeader ? {marginRight:marginRight,position:'relative',top:'0.9rem',outline:'#4E6F92 1px solid',display:'inline-block'} : {},
    };


    return (
        <>
            {/* Apply Button */}
            <button {...commonProps} onClick={handleButtonClick}>
                {text}
            </button>

            {/* Interstitial Modal */}
            {showInterstitial && (
                <ApplyInterstitialFormRHF
                    communityName={communityName}
                    slug={slug}
                    onSubmit={handleInterstitialSubmit}
                    onSkip={handleInterstitialSkip}
                    onClose={handleInterstitialClose}
                    arrivalInfo={arrivalInfo}
                    sessionId={arrivalInfo?.sessionId}
                    buttonPosition={finalPosition}
                    floorplanName={floorplanName || itemName}
                    floorplanBeds={floorplanBeds}
                    toEmail={toEmail}
                    trackEventFunction={trackApplyEvent}
                />
            )}
        </>
    );
}

export default ApplyButton
